'use client';

import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { buttonVariants } from '@eliadmin/core/src/components/ui/button';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import type { Locale } from 'date-fns';
import { cs, de, enUS, fr, sk } from 'date-fns/locale';
import * as React from 'react';
import { DateLib, DayPicker, getDefaultClassNames } from 'react-day-picker';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

export const localeMapping: Record<string, Locale> = {
  sk,
  'sk-SK': sk,
  cs,
  cz: cs,
  'cs-CZ': cs,
  de,
  'de-DE': de,
  fr,
  'fr-FR': fr,
  en: enUS,
  'en-US': enUS,
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  weekStartsOn = 1,
  components,
  formatters,
  captionLayout = 'dropdown',
  autoFocus,
  initialFocus,
  fromYear,
  toYear,
  fromMonth,
  toMonth,
  startMonth,
  endMonth,
  ...props
}: CalendarProps) {
  const localeCode = useLocaleStore(state => state.currentLocale);
  const locale = props.locale ?? localeMapping[localeCode] ?? enUS;
  const currentYear = new Date().getFullYear();
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      weekStartsOn={weekStartsOn}
      locale={locale}
      captionLayout={captionLayout}
      autoFocus={autoFocus ?? initialFocus}
      startMonth={startMonth ?? fromMonth ?? new Date(fromYear ?? currentYear - 10, 0)}
      endMonth={endMonth ?? toMonth ?? new Date(toYear ?? currentYear + 10, 11)}
      className={cn('w-fit p-3', className)}
      classNames={{
        ...classNames,
        root: cn('relative', defaultClassNames.root, classNames?.root),
        months: cn('relative flex flex-col gap-4 sm:flex-row', classNames?.months),
        month: cn('flex w-[280px] flex-col gap-4', classNames?.month),
        month_caption: cn(
          'relative flex h-8 items-center justify-center px-9',
          classNames?.month_caption,
        ),
        caption_label: cn(
          'flex items-center gap-1 px-2 py-1 text-sm font-semibold capitalize',
          classNames?.caption_label,
        ),
        dropdowns: cn('flex items-center justify-center gap-1', classNames?.dropdowns),
        dropdown_root: cn('relative rounded-md border border-input', classNames?.dropdown_root),
        dropdown: cn(
          'absolute inset-0 cursor-pointer opacity-0',
          defaultClassNames.dropdown,
          classNames?.dropdown,
        ),
        months_dropdown: cn('appearance-none bg-background px-2 py-1', classNames?.months_dropdown),
        years_dropdown: cn('appearance-none bg-background px-2 py-1', classNames?.years_dropdown),
        nav: cn(
          'pointer-events-none absolute inset-x-0 top-0 z-10 flex items-center justify-between',
          classNames?.nav,
        ),
        button_previous: cn(
          buttonVariants({ variant: 'outline' }),
          'pointer-events-auto h-8 w-8 bg-transparent p-0 text-foreground opacity-70 hover:opacity-100',
          classNames?.button_previous,
        ),
        button_next: cn(
          buttonVariants({ variant: 'outline' }),
          'pointer-events-auto h-8 w-8 bg-transparent p-0 text-foreground opacity-70 hover:opacity-100',
          classNames?.button_next,
        ),
        month_grid: cn('w-full table-fixed border-collapse', classNames?.month_grid),
        weekdays: cn('border-0', classNames?.weekdays),
        weekday: cn(
          'h-9 w-9 p-0 text-center text-xs font-normal text-muted-foreground',
          classNames?.weekday,
        ),
        weeks: cn(classNames?.weeks),
        week: cn('border-0', classNames?.week),
        week_number_header: cn('w-9', classNames?.week_number_header),
        week_number: cn('w-9 text-center text-xs text-muted-foreground', classNames?.week_number),
        day: cn(
          'relative h-9 w-9 p-0 text-center text-sm [&:has([aria-selected])]:rounded-md',
          classNames?.day,
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-9 w-9 p-0 font-normal text-inherit aria-selected:opacity-100',
          classNames?.day_button,
        ),
        selected: cn(
          '[&:not(.day-range-middle)]:rounded-md [&:not(.day-range-middle)]:bg-primary [&:not(.day-range-middle)]:text-primary-foreground [&:not(.day-range-middle)>button]:bg-primary [&:not(.day-range-middle)>button]:text-primary-foreground',
          classNames?.selected,
        ),
        range_start: cn('rounded-l-md', classNames?.range_start),
        range_middle: cn(
          'day-range-middle rounded-none bg-accent text-accent-foreground [&>button]:rounded-none [&>button]:bg-accent [&>button]:text-accent-foreground',
          classNames?.range_middle,
        ),
        range_end: cn('rounded-r-md', classNames?.range_end),
        today: cn(
          'rounded-md bg-accent text-accent-foreground [&:not([data-selected])>button]:bg-accent',
          classNames?.today,
        ),
        outside: cn('text-muted-foreground opacity-45', classNames?.outside),
        disabled: cn('text-muted-foreground opacity-45', classNames?.disabled),
        hidden: cn('invisible', classNames?.hidden),
        chevron: cn('size-4 fill-current', classNames?.chevron),
      }}
      components={{
        Chevron: ({ orientation, className: chevronClassName }) => {
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('size-4 text-foreground', chevronClassName)} />;
          }
          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('size-4 text-foreground', chevronClassName)} />;
          }
          return <ChevronDownIcon className={cn('size-4', chevronClassName)} />;
        },
        ...components,
      }}
      formatters={{
        formatWeekdayName: (date, options, dateLib) =>
          (dateLib ?? new DateLib(options)).format(date, 'EE'),
        ...formatters,
      }}
      {...props}
    />
  );
}

Calendar.displayName = 'Calendar';

export { Calendar };
